import { useState } from 'react'
import { useFailedMutations } from '@/hooks/pwa/useFailedMutations'
import { CloseIcon } from '@/icons'

export default function FailedMutationsSheet() {
  const { failedMutations, retryMutation, discardMutation, retryAll, discardAll, hasFailedMutations } =
    useFailedMutations()
  // Track dismissed failures by mutationId rather than a sticky boolean: a new
  // failure (an id not in this snapshot) re-opens the sheet, so dismissing once
  // can't permanently hide later failed offline writes — this is the only
  // surface for mutations that exhausted retries.
  const [dismissedIds, setDismissedIds] = useState<ReadonlySet<number>>(() => new Set())

  const hasUndismissed = failedMutations.some((m) => !dismissedIds.has(m.mutationId))
  if (!hasFailedMutations || !hasUndismissed) return null

  const dismiss = () => setDismissedIds(new Set(failedMutations.map((m) => m.mutationId)))

  return (
    <div
      role="region"
      aria-live="polite"
      aria-label="Failed changes"
      data-testid="failed-mutations-sheet"
      className="fixed bottom-0 left-0 right-0 z-50 md:bottom-4 md:left-auto md:right-4 md:max-w-sm"
    >
      <div className="bg-surface border-t md:border md:rounded-lg border-red-500/50 shadow-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b border-theme-border">
          <h3 className="text-sm font-medium text-red-400">
            {failedMutations.length} failed change{failedMutations.length !== 1 ? 's' : ''}
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={retryAll}
              className="text-xs px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
            >
              Retry all
            </button>
            <button
              onClick={discardAll}
              className="text-xs px-2 py-1 bg-subtle-bg hover:bg-hover text-primary rounded transition-colors"
            >
              Discard all
            </button>
            <button
              onClick={dismiss}
              className="p-1 hover:bg-hover rounded transition-colors text-muted"
              aria-label="Dismiss"
            >
              <CloseIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
        <ul className="max-h-48 overflow-y-auto divide-y divide-theme-border">
          {failedMutations.map((m) => (
            <li key={m.mutationId} className="px-4 py-2 flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-secondary truncate">{m.label}</p>
                <p className="text-xs text-red-400 truncate">{m.error?.message ?? 'Unknown error'}</p>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => retryMutation(m.mutationId)}
                  className="text-xs px-2 py-1 text-blue-400 hover:bg-hover rounded transition-colors"
                >
                  Retry
                </button>
                <button
                  onClick={() => discardMutation(m.mutationId)}
                  className="text-xs px-2 py-1 text-muted hover:bg-hover rounded transition-colors"
                >
                  Discard
                </button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
